import { MemoizedReactMarkdown } from '@/components/ui/markdown'
import { motion } from 'framer-motion'
import rehypeExternalLinks from 'rehype-external-links'
import remarkGfm from 'remark-gfm'
import { useTheme } from '../lib/themeContext'
import posthog from 'posthog-js'
import rehypeRaw from 'rehype-raw'

export function Message({
  message,
  direction
}: {
  message: string
  direction: string
}) {
  const { primary, accent } = useTheme()

  const phoneRegex = /\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}/g
  // Regex to detect URLs (http, https only)
  const linkRegex = /(https?:\/\/[^\s<>"'`&]+(?![a-zA-Z#0-9]{1,6};))/g

  function linkifyPhoneNumbers(text: string) {
    return text.replace(phoneRegex, match => {
      const digitsOnly = match.replace(/\D/g, '')
      return `[${match}](tel:${digitsOnly})`
    })
  }

  function linkifyUrls(text: string) {
    // Check if already contains Markdown or HTML links to avoid reprocessing
    const hasMarkdownLinks = /\[.*?\]\(.*?\)/.test(text)
    const hasHtmlLinks = /<a\s+[^>]*href/.test(text)

    if (hasMarkdownLinks || hasHtmlLinks) {
      return text
    }

    // Process bare URLs in text (only http:// and https:// URLs)
    return text.replace(linkRegex, (match, offset, string) => {
      // Convert to Markdown link format
      return `[${match}](${match})`
    })
  }

  const isHTML = (content: string) => {
    const htmlTagRegex = /<[^>]*>/g
    const markdownLinkRegex = /\[.*?\]\(.*?\)/g
    const contentWithoutMdLinks = content.replace(markdownLinkRegex, '')

    return htmlTagRegex.test(contentWithoutMdLinks)
  }

  const preprocessContent = (content: string) => {
    if (typeof content !== 'string') {
      return ''
    }

    const partialImageMarkdownRegex = /\[(.*?)\]\((https?:\/\/[^\s\)]*)$/g
    let processedContent = content.replace(
      partialImageMarkdownRegex,
      (_, alt) => `Loading...`
    )

    if (isHTML(content)) {
      // Process phone numbers in HTML content
      processedContent = processedContent.replace(
        phoneRegex,
        (match, offset) => {
          const beforeMatch = processedContent.substring(0, offset)
          // Don't process if already inside an <a> tag
          const lastOpenTag = beforeMatch.lastIndexOf('<a')
          const lastCloseTag = beforeMatch.lastIndexOf('</a>')
          if (lastOpenTag > lastCloseTag) {
            return match
          }
          const digitsOnly = match.replace(/\D/g, '')
          return `<a href="tel:${digitsOnly}" class="not-prose underline">${match}</a>`
        }
      )
      // For HTML, convert bare URLs to HTML links
      processedContent = processedContent.replace(
        linkRegex,
        (match, offset) => {
          const beforeMatch = processedContent.substring(0, offset)
          // Don't process if already inside an <a> tag
          const lastOpenTag = beforeMatch.lastIndexOf('<a')
          const lastCloseTag = beforeMatch.lastIndexOf('</a>')
          if (lastOpenTag > lastCloseTag) {
            return match
          }
          // Don't process if already inside a Markdown link
          const lastOpenBracket = beforeMatch.lastIndexOf('[')
          const lastCloseParen = beforeMatch.lastIndexOf(')')
          if (lastOpenBracket > lastCloseParen) {
            return match
          }
          return `<a href="${match}" class="not-prose underline" target="_blank" rel="noopener noreferrer">${match}</a>`
        }
      )
    } else {
      // Process phone numbers and URLs in Markdown content
      processedContent = linkifyPhoneNumbers(processedContent)
      processedContent = linkifyUrls(processedContent)
    }

    return processedContent
  }

  const handleLinkClick = (href: string | undefined, children: any) => {
    posthog.capture('link_clicked', {
      href: href,
      linkText: typeof children === 'string' ? children : 'non-text link'
    })
  }

  const renderContent = (content: string) => {
    const processedContent = preprocessContent(content)

    return (
      <MemoizedReactMarkdown
        rehypePlugins={[
          [rehypeRaw], // This allows HTML content to be processed
          [rehypeExternalLinks, { target: '_blank' }]
        ]}
        remarkPlugins={[remarkGfm]}
        className="prose-sm prose-neutral prose-a:text-accent-foreground/50"
        components={{
          a: ({ href, children, ...props }) => {
            return (
              <a
                style={{ color: primary }}
                className="not-prose underline"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => handleLinkClick(href, children)}
                {...props}
              >
                {children}
              </a>
            )
          },
          // Handle HTML elements that might contain phone numbers
          p: ({ children, ...props }) => <p {...props}>{children}</p>,
          div: ({ children, ...props }) => <div {...props}>{children}</div>,
          span: ({ children, ...props }) => <span {...props}>{children}</span>
        }}
      >
        {processedContent}
      </MemoizedReactMarkdown>
    )
  }

  return direction === 'left' ? (
    <div
      key={message}
      className="max-w-[90%] self-start rounded-[10px] p-2.5 text-sm font-semibold"
      style={{ backgroundColor: '#F3F3F3' }}
    >
      {renderContent(message)}
    </div>
  ) : (
    <motion.div
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      key={message}
      className="max-w-[90%] self-end rounded-[30px] px-4 py-2 text-sm font-semibold text-white"
      style={{ backgroundColor: primary === '#ffffff' ? accent : primary }}
    >
      <MemoizedReactMarkdown
        rehypePlugins={[[rehypeExternalLinks, { target: '_blank' }]]}
        remarkPlugins={[remarkGfm]}
        className="prose-sm prose-neutral prose-a:text-accent-foreground/50"
      >
        {linkifyPhoneNumbers(message)}
      </MemoizedReactMarkdown>
    </motion.div>
  )
}
